import { FC } from 'react'
import { FeatureCollection, Point } from 'geojson'
import { Layer, Source } from 'react-map-gl'

/**
 * GeoPointMarkerLayer component displays point markers on a map using GeoJSON data.
 *
 * Props:
 * - `key`: Unique key to identify the source, ensuring it re-renders as necessary.
 * - `id`: Unique identifier for the layer to ensure map layers are distinct.
 * - `promoteId`: Field used as a unique identifier for each feature in the data source.
 * - `featureCollection`: GeoJSON feature collection containing point data for markers.
 */
interface GeoPointMarkerLayerProps {
  key: string | number
  id: string | number
  promoteId: string
  featureCollection: FeatureCollection<Point>
}

/**
 * Derives the source and layer IDs that GeoPointMarkerLayer uses for a given base id.
 * Use this in consumers (setFeatureState, setupHoverEffect, source/layer lookups)
 * so the naming convention lives in one place.
 */
export const getGeoPointMarkerLayerIds = (id: string | number) => {
  const unselectedLayer = `${id}-symbol-layer-unselected`
  const selectedLayer = `${id}-symbol-layer-selected`
  return {
    source: `${id}-shape-source`,
    unselectedLayer,
    selectedLayer,
    layers: [unselectedLayer, selectedLayer],
  }
}

export const GeoPointMarkerLayer: FC<GeoPointMarkerLayerProps> = ({
  key,
  id,
  promoteId,
  featureCollection,
}) => {
  return (
    <Source
      key={`${key}-shape-source`}
      id={`${id}-shape-source`}
      type='geojson'
      promoteId={promoteId}
      data={featureCollection}
    >
      {/* Layer for unselected markers */}
      <Layer
        id={`${id}-symbol-layer-unselected`}
        type='symbol'
        layout={{
          'icon-image': 'unselected-marker-icon',
          'icon-allow-overlap': true,
        }}
        paint={{
          // Visible only when neither selected nor current
          'icon-opacity': [
            'case',
            ['==', ['coalesce', ['feature-state', 'current'], false], true],
            0,
            ['==', ['coalesce', ['feature-state', 'selected'], false], true],
            0,
            1,
          ],
        }}
        beforeId={`${id}-symbol-layer-selected`}
      />

      {/* Layer for selected (hover) markers */}
      <Layer
        id={`${id}-symbol-layer-selected`}
        type='symbol'
        layout={{
          'icon-image': 'selected-marker-icon',
          'icon-allow-overlap': true,
        }}
        paint={{
          // Visible only when selected and not current
          'icon-opacity': [
            'case',
            ['==', ['coalesce', ['feature-state', 'current'], false], true],
            0,
            ['==', ['coalesce', ['feature-state', 'selected'], false], true],
            1,
            0,
          ],
        }}
        beforeId={`${id}-symbol-layer-current`}
      />

      {/* Layer for the current (highlighted) marker */}
      <Layer
        id={`${id}-symbol-layer-current`}
        type='symbol'
        layout={{
          'icon-image': 'current-marker-icon',
          'icon-allow-overlap': true,
        }}
        paint={{
          'icon-opacity': [
            'case',
            ['==', ['coalesce', ['feature-state', 'current'], false], true],
            1,
            0,
          ],
        }}
      />
    </Source>
  )
}
